import { Box, Button, Divider, IconButton, InputAdornment, Link, Stack, Typography } from '@mui/material';
import { Visibility as ShowIcon, VisibilityOff as HideIcon } from '@mui/icons-material';
import { ReactNode, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormTextField } from '@/components/HookForm';
import { dialogOpen } from '@/modules/MuiDialog';
import ResetPassword from '@/pages/dialogs/ResetPassword';
import { apiUrls } from '@/constant/configure';
import { AuthBrand } from './AuthBrandPanel';
import AuthHeadline from './AuthHeadline';
import SocialLoginButtons from './SocialLoginButtons';
import soduBrandMark from '@/assets/sodu/brand-mark.svg';
import soduWordmarkStack from '@/assets/sodu/wordmark-stack.svg';

export interface AuthFormProps {
    /** 소셜 인가 요청의 state 로 실려 로그인 후 돌아올 경로 */
    refer?: string | null;
    /** 이메일/비밀번호 제출 처리. 성공/실패 처리와 이동은 호출자가 책임진다. */
    onSubmitCredentials: (email: string, password: string) => Promise<void>;
    /** 소셜 로그인 노출 여부 */
    showSocial?: boolean;
    /** 하단 회원가입 안내 노출 여부 */
    showSignUpLink?: boolean;
    /** 순환 헤드라인 노출 여부 (모달에서는 끈다) */
    showHeadline?: boolean;
    /** 모바일 상단 브랜드 로고 노출 여부 (전체 페이지에서만) */
    showMobileBrand?: boolean;
    /** 모달 등 좁은 폭에서 여백을 줄인다 */
    dense?: boolean;
    /** 이메일 초기값 (예: ?email= 로 넘어온 셀러 초대) */
    defaultEmail?: string;
    /** 모바일 상단에 쓸 브랜드 자산 */
    brand?: AuthBrand;
    /** 순환 헤드라인 대신 쓸 제목. 주면 헤드라인을 대체한다 */
    title?: ReactNode;
    /** 기본 서브카피를 대체 */
    subtitle?: string;
    /** 비밀번호 찾기 동작 override (기본: ResetPassword 다이얼로그) */
    onForgotPassword?: (email: string) => void;
    /**
     * 제출 버튼 액센트 색 override.
     * 소도몰 파트너 화면처럼 앱 primary(핑크)와 다른 브랜드 색을 쓰는 곳에서만 준다.
     */
    accentColor?: string;
}

/**
 * 로그인 폼 본문 — 글로릴스 로그인 화면과 같은 순서·위계.
 *
 *   헤드라인 → 서브카피 → 소셜(우선) → "또는 이메일로" → 이메일 폼 → 회원가입 안내
 *
 * 전체 페이지(`pages/Login.tsx`)와 모달(`pages/dialogs/LoginDialog.tsx`)이 공유한다.
 */
export default function AuthForm(props: AuthFormProps) {
    const {
        refer,
        onSubmitCredentials,
        showSocial = true,
        showSignUpLink = true,
        showHeadline = true,
        showMobileBrand = false,
        dense = false,
        defaultEmail = '',
        brand,
        title,
        subtitle,
        onForgotPassword,
        accentColor,
    } = props;

    const { t } = useTranslation();
    const form = useForm<FieldValues>({ defaultValues: { email: defaultEmail, password: '' } });
    const {
        handleSubmit,
        getValues,
        formState: { isValid, isSubmitting },
    } = form;
    const [isPassView, setIsPassView] = useState(false);

    async function submit(data: any) {
        if (!isValid || isSubmitting) return;
        await onSubmitCredentials(String(data.email ?? '').trim(), data.password);
    }

    return (
        <Box sx={{ width: '100%', maxWidth: 384 }}>
            {showMobileBrand && (
                <Box
                    sx={{
                        display: { xs: 'flex', lg: 'none' },
                        alignItems: 'center',
                        gap: 1.25,
                        mb: 5,
                    }}
                >
                    <Box
                        component="img"
                        src={brand?.mark ?? soduBrandMark}
                        alt=""
                        sx={{ width: 32, height: 32, objectFit: 'contain' }}
                    />
                    {brand?.wordmark || !brand ? (
                        <Box
                            component="img"
                            src={brand?.wordmark ?? soduWordmarkStack}
                            alt={brand?.label ?? '소셜 듀오'}
                            sx={{ height: 28, width: 'auto', objectFit: 'contain' }}
                        />
                    ) : (
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {brand.label}
                        </Typography>
                    )}
                </Box>
            )}

            {title ?? (showHeadline && <AuthHeadline compact={dense} />)}

            <Typography
                variant="body2"
                sx={{ mt: title || showHeadline ? 1.5 : 0, mb: dense ? 3 : 4, color: 'text.secondary', lineHeight: 1.7 }}
            >
                {subtitle ?? '로그인하고 이어서 시작하세요.'}
            </Typography>

            {showSocial && (
                <>
                    <SocialLoginButtons refer={refer} />

                    <Box sx={{ my: dense ? 2.5 : 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            또는 이메일로
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                    </Box>
                </>
            )}

            <form onSubmit={handleSubmit(submit)}>
                <Stack spacing={0.5}>
                    <FormTextField
                        label={t('auth.email')}
                        placeholder={'account@email.com'}
                        name={'email'}
                        form={form}
                        rules={[(v: string) => !!v || t('auth.emailRequired')]}
                        fullWidth
                    />
                    <FormTextField
                        label={t('auth.password')}
                        placeholder={'8자 이상'}
                        name={'password'}
                        form={form}
                        rules={[(v: string) => !!v || t('auth.passwordRequired')]}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position={'end'}>
                                    <IconButton sx={{ cursor: 'pointer' }} onClick={() => setIsPassView(!isPassView)}>
                                        {isPassView ? <HideIcon /> : <ShowIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        type={isPassView ? 'text' : 'password'}
                        fullWidth
                    />
                </Stack>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, mb: 2 }}>
                    <Button
                        size="small"
                        sx={{ cursor: 'pointer', color: 'text.secondary', fontWeight: 500 }}
                        onClick={() => {
                            const email = getValues('email');
                            if (onForgotPassword) {
                                onForgotPassword(email);
                                return;
                            }
                            dialogOpen({ innerElement: <ResetPassword email={email} /> });
                        }}
                    >
                        {t('auth.forgotPassword')}
                    </Button>
                </Box>

                <Button
                    type={'submit'}
                    variant={'contained'}
                    size={'large'}
                    color={'primary'}
                    fullWidth
                    disabled={isSubmitting}
                    sx={{
                        cursor: 'pointer',
                        borderRadius: '12px',
                        py: 1.35,
                        fontWeight: 700,
                        boxShadow: 'none',
                        transition: 'transform .16s cubic-bezier(.2,.8,.2,1)',
                        willChange: 'transform',
                        ...(accentColor
                            ? {
                                  bgcolor: accentColor,
                                  '&:hover': { boxShadow: 'none', transform: 'translateY(-1px)', bgcolor: accentColor, filter: 'brightness(1.08)' },
                              }
                            : { '&:hover': { boxShadow: 'none', transform: 'translateY(-1px)' } }),
                        '&:active': { transform: 'translateY(0) scale(0.985)' },
                    }}
                >
                    {isSubmitting ? t('common.loading') : t('auth.login')}
                </Button>
            </form>

            {showSignUpLink && (
                <Typography variant="body2" sx={{ mt: dense ? 3 : 4, textAlign: 'center', color: 'text.secondary' }}>
                    아직 계정이 없으신가요?{' '}
                    <Link
                        href={`/register/sign-up`}
                        sx={{ cursor: 'pointer', fontWeight: 700, color: 'text.primary', textUnderlineOffset: '3px' }}
                    >
                        회원가입
                    </Link>
                </Typography>
            )}
        </Box>
    );
}
